import React, { useState, useEffect, useMemo } from "react";
import {
  Button,
  Dialog,
  TextField,
  FormLabel,
  CircularProgress
} from "@material-ui/core";
import useTranslation from "Hooks/useTranslation";
import AlertToastr from "utils/alert";
import { useAuth } from "../../../../Contexts/AuthProvider";
import { Autocomplete } from '@material-ui/lab';
import { normalizeErrorResponse } from "utils/form";
import MmsService from "Services/MMS/mms-api.service";

const AttachComponentModal = ({
  isOpen,
  handleClose,
  departmentId,
  selectedComponent,
  components,
  onSuccessAttach,
  isOpenManager,
  mode = "attach", 
}) => {
  const emptyComponentOption = {
    id: "empty",
    name: "Select component to attach",
  };

  
  const translation = useTranslation();
  const { currentCompany } = useAuth();
  const { currentUser } = useAuth();
  const isSuperUser = useMemo(() => currentUser.isSuperuser(), [currentUser]);

  const [loading, setLoading] = useState(false);
  const [selectedTargetComponent, setSelectedTargetComponent] = useState(emptyComponentOption);
  const [errors, setErrors] = useState({});
  const [disableButtons, setDisabledButtons] = useState(false);
 
  const availableComponents = useMemo(() => {
    if (!components || !selectedComponent || !selectedComponent.id) return [];
    
    const filterComponents = (comps, excludeIds) => {
      return comps.filter(comp => {
        // Перевіряємо, чи comp існує і має id
        if (!comp || !comp.id) return false;
        
        if (excludeIds.includes(comp.id)) return false;
        
        if (comp.subComponents && comp.subComponents.length > 0) {
          comp.subComponents = filterComponents(comp.subComponents, excludeIds);
        }
        
        return true;
      });
    };

    const excludeIds = [selectedComponent.id];
    const collectSubComponentIds = (comp) => {
      if (comp && comp.subComponents && comp.subComponents.length > 0) {
        comp.subComponents.forEach(subComp => {
          if (subComp && subComp.id) {
            excludeIds.push(subComp.id);
            collectSubComponentIds(subComp);
          }
        });
      }
    };
    collectSubComponentIds(selectedComponent);

    return filterComponents(components, excludeIds);
  }, [components, selectedComponent]);

  const handleAutocompleteChange = (event, newValue) => {
    setErrors({});
    if (newValue && newValue.id !== "empty") {
      setSelectedTargetComponent(newValue);
    } else {
      setSelectedTargetComponent(emptyComponentOption);
    }
  };

  const handleAttachButtonClick = () => {
    if (mode === "detach") {
      handleDetachButtonClick();
      return;
    }

    if (selectedTargetComponent.id === "empty") {
      setErrors({ targetComponent: ["Please select a component to attach"] });
      return;
    }

    setLoading(true);
    setDisabledButtons(true);
   
    MmsService.attachComponent(
      selectedComponent.id,
      selectedTargetComponent.id,
      departmentId
    )
      .then((response) => {
        if (response.data.isSuccessful) {
          if (onSuccessAttach) onSuccessAttach(selectedComponent, selectedTargetComponent);
          isOpenManager(false);
          setErrors({});
          setSelectedTargetComponent(emptyComponentOption);
          setLoading(false);
          AlertToastr.showAlert(
            response.data.message || `Component ${selectedComponent.name} has been successfully attached to ${selectedTargetComponent.name}`
          );
          handleCloseModal();
          setDisabledButtons(false);
        }
      })
      .catch((error) => {
        setLoading(false);
        setDisabledButtons(false);
        if (!error || !error.response || !error.response.data) return;
        const resErrors = normalizeErrorResponse(error.response.data);
        if (resErrors.errors) setErrors(resErrors.errors);
        if (resErrors && resErrors.message)
          AlertToastr.showErrorAlert(resErrors.message);
      });
  };

  const handleDetachButtonClick = () => {
    setLoading(true);
    setDisabledButtons(true);
   
    MmsService.detachComponent(selectedComponent.id)
      .then((response) => {
        if (response.data.isSuccessful) {
          const detachTime = Date.now();
          window.lastAttachTime = detachTime;;
          
          if (onSuccessAttach) onSuccessAttach();
          isOpenManager(false);
          setErrors({});
          setLoading(false);
          AlertToastr.showAlert(
            response.data.message || `Component ${selectedComponent.name} has been successfully detached`
          );
          handleCloseModal();
          setDisabledButtons(false);
        }
      })
      .catch((error) => {
        setLoading(false);
        setDisabledButtons(false);
        if (!error || !error.response || !error.response.data) return;
        const resErrors = normalizeErrorResponse(error.response.data);
        if (resErrors.errors) setErrors(resErrors.errors);
        if (resErrors && resErrors.message)
          AlertToastr.showErrorAlert(resErrors.message);
      });
  };

  const handleCloseModal = () => {
    setSelectedTargetComponent(emptyComponentOption);
    setErrors({});
    handleClose();
  };

  const getTitle = () => {
    return mode === "detach" ? "Detach Component" : "Attach Component";
  };

  const getActionButtonText = () => {
    return mode === "detach" ? "Detach" : "Attach";
  };

  const getActionButtonId = () => {
    return mode === "detach" ? "detachModalDetachBtn" : "attachModalAttachBtn";
  };

  return (
    <Dialog
      open={isOpen}
      onClose={(e) => {
        handleCloseModal();
        setErrors({});
      }}
      className="respModal autoHeight"
      style={{ width: "448px", marginLeft: "auto", marginRight: "auto" }}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          handleAttachButtonClick();
        }
        if (e.key === "Escape") {
          handleCloseModal();
        }
      }}
    >
      <div className="mb16">
        <h1 className="workhoursUpdateModalTitle">
          {getTitle()}
        </h1>
      </div>
      <div className="mb6">
        <h2 className="workhoursUpdateJobTitle">
          {selectedComponent?.fullName || selectedComponent?.name}
        </h2>
      </div>
      {mode === "detach" ? (
        <div className="mb16">
          <p className="textGray">
            Are you sure you want to detach the component?
          </p>
        </div>
      ) : (
        <div>
          <FormLabel component="legend" className="mb5">
            Attach to:
          </FormLabel>
          <div className="">
            <Autocomplete
              id="attachModalSelectComponent"
              className='autocompletePartActivity'
              options={[emptyComponentOption, ...availableComponents]}
              getOptionLabel={(option) => {
                if (option.id === "empty") return option.name;
                return option.uId ? `${option.name} (${option.uId})` : option.name;
              }}
              onChange={handleAutocompleteChange}
              value={selectedTargetComponent}
              renderInput={(params) => (
                <TextField
                  {...params}
                  id="attachModalSelectComponentChoose"
                  label="Select component to attach to"
                  variant="outlined"
                  margin="normal"
                  fullWidth
                  className="mb16"
                  error={
                    errors &&
                    errors.targetComponent &&
                    errors.targetComponent.length > 0
                  }
                  helperText={
                    errors &&
                    errors.targetComponent &&
                    errors.targetComponent.join(", ")
                  }
                />
              )}
            />
          </div>
        </div>
      )}
      <div className="btnHolder end fixedWidthBtn">
        <Button
          variant="outlined"
          id="attachModalCancelBtn"
          fullWidth
          onClick={handleCloseModal}
        >
          {translation.cancelButtonLabel}
        </Button>       
        <div className="part">
          <Button
            variant="contained"
            color="primary"
            fullWidth
            id={getActionButtonId()}
            style={{ marginBottom: "0" }}
            onClick={handleAttachButtonClick}
            disabled={!!disableButtons || loading}
          >
            {getActionButtonText()}
          </Button>
          {loading && <CircularProgress size={24} />}
        </div>
      </div>
    </Dialog>
  );
};

export default AttachComponentModal; 